<template>
    <div class="d-flex" style="height: 800px">
        <div
            ref="content"
            id="content-container"
            class="overflow-auto px-0 w-100 d-flex flex-column nch--content-container col-lg-12"
        >
            <ExerciseHeader
                v-if="showHeader"
                :exercise-progress="exerciseProgress"
                :certificate="certificate"
                class="py-2"
            >
                <template #navigation><NavigationControl :link="link" /></template>
            </ExerciseHeader>
            <div class="my-0" :class="stretch ? 'd-flex stretch w-100' : ''">
                <div
                    v-if="exercise.started_confirmation || exerciseFinished || !exercise.time"
                    class="container mt-5"
                >
                    <div class="row">
                        <div class="col xs-8">
                            <div class="card px-3 py-4">
                                <div v-if="exerciseFinished" class="exercise-score d-flex mb-3">
                                    <h2 class="my-auto mx-auto">
                                        <i class="mdi mdi-octagram"></i>
                                        {{$t('common.final_grade')}}: {{exercise.grade}}
                                    </h2>
                                    <h2 class="my-auto mx-auto">
                                        <i class="mdi mdi-circle-slice-1"></i>
                                        {{$t('content.exercise.utilization')}}: {{exercise.grade_percentage}}%
                                    </h2>
                                </div>
                                <h2 class="card-title">{{$t('content.exercise.questions')}}</h2>
                                <div
                                    v-for="(question, index) in exercise.questions"
                                    :key="question.id"
                                >
                                    <SingleAnswer
                                        v-if="question.type === 1"
                                        :question="question"
                                        :index="index"
                                        :isFinished="exerciseFinished"
                                        :ref="`question-${question.id}`"
                                        :verifyValidate="validError"
                                    />
                                    <MultipleAnswer
                                        v-if="question.type === 2"
                                        :question="question"
                                        :index="index"
                                        :isFinished="exerciseFinished"
                                        :ref="`question-${question.id}`"
                                        :verifyValidate="validError"
                                    />
                                    <DissertationAnswer
                                        v-if="question.type === 3"
                                        :question="question"
                                        :index="index"
                                        :isFinished="exerciseFinished"
                                        :ref="`question-${question.id}`"
                                    />
                                    <UploadAnswer
                                        v-if="question.type === 4"
                                        :index="index"
                                        :question="question"
                                        :isFinished="exerciseFinished"
                                        :ref="`question-${question.id}`"
                                        :verifyValidate="validError"
                                    />
                                </div>
                                <base-button
                                    v-if="exerciseFinished && exercise.tries"
                                    class="mt-5"
                                    :style="[$bgColor(color.primary), { color: color.secondary }]"
                                    @click="redo"
                                >
                                    {{$t('lesson.redo')}}
                                </base-button>
                                <base-button
                                    v-else-if="exercise.tries"
                                    class="mt-5"
                                    :style="[$bgColor(color.primary), { color: color.secondary }]"
                                    @click="submit"
                                >
                                    {{$t('lesson.finish_exercise')}}
                                </base-button>
                                <p v-if="error" class="nch-alert mt-3">
                                    <i class="mdi mdi-alert"></i>
                                    {{error}}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <TabOptions v-if="$attrs.lesson" :lesson="$attrs.lesson" />
            </div>
            <template v-if="exercise.forum">
                <ExerciseForumTab :exercise="exercise"></ExerciseForumTab>
            </template>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import SingleAnswer from './ExerciseComponents/ExerciseSingleAnswer';
import MultipleAnswer from './ExerciseComponents/ExerciseMultipleAnswer';
import DissertationAnswer from './ExerciseComponents/ExerciseDissertation';
import UploadAnswer from './ExerciseComponents/ExerciseUpload';
import ExerciseHeader from './ExerciseComponents/ExerciseHeader';
import TabOptions from '../components/Common/TabOptions/TabOptions';
import NavigationControl from '../components/Common/NavigationControl';
import ExerciseForumTab from './ExerciseComponents/ExerciseForumTab';

import BaseButton from '../argon/components/BaseButton';

export default {
    name: 'Exercise',
    components: {
        SingleAnswer,
        MultipleAnswer,
        DissertationAnswer,
        BaseButton,
        UploadAnswer,
        TabOptions,
        ExerciseHeader,
        NavigationControl,
        ExerciseForumTab,
    },

    props: {
        certificate: Object,
        data: [Object, null],
        exerciseFinal: Object,
        exerciseProgress: Boolean,
        showHeader: Boolean,
        stretch: Boolean,
        parents: Object,
        isPublic: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            exerciseFinished: false,
            validError: false,
            loadedCertificate: null,
        };
    },

    watch: {
        exercise: {
            handler(newVal) {
                this.exerciseFinished = !!newVal.finished;
            },
            immediate: true,
            deep: true,
        },
    },

    computed: {
        ...mapGetters({
            exercise: 'LOADED_EXERCISE',
            color: 'COLOR',
            error: 'EXERCISE_ERROR',
            link: 'LOADED_CONTENT_LINKS',
        }),

        submodules() {
            const submodules = new URLSearchParams(window.location.search);
            return submodules.getAll('submodules[]');
        },

    },

    methods: {
        configSlug() {
            const slug = {};
            const keys = Object.keys(this.parents);
            const newKeys = keys.map(key => key.replace(/Id$/, ''));

            newKeys.forEach((newKey, index) => {
                slug[newKey] = parseInt(this.parents[keys[index]], 10);
            });

            if (this.submodules.length) {
                const submodulesIDs = this.submodules.map(id => parseInt(id, 10));
                slug.submodules = submodulesIDs;
            }
            return slug;
        },

        async submit() {
            const success = this.exercise.questions.map(question => this.$refs[`question-${question.id}`][0].$validator.validateAll());

            const promises = await Promise.all(success);
            const valid = promises.every(validation => validation);
            if (valid) {
                this.validError = false;
                this.$loading.show();

                this.$store.dispatch('SUBMIT_EXERCISE', {
                    id: this.exercise.id,
                    slug: this.configSlug(),
                });
                this.$emit(
                    'contentSubmited',
                    this.exerciseFinal ? this.exerciseFinal.final : null,
                );
                this.$refs.content.scrollTop = 0;

                this.$store.commit('CLEAR_ERROR');
                this.$loading.close();
            } else {
                this.validError = true;
                this.$notify({
                    type: 'danger',
                    message: this.$t('exercise.validate.some_question_no_anwser'),
                });
            }
        },
        async redo() {
            this.$loading.show();
            await this.$store.dispatch('REDO_EXERCISE', this.exercise.id);
            this.$emit('contentSubmited');
            this.$refs.content.scrollTop = 0;

            if (this.exercise.time) {
                await this.$store.dispatch('GET_EXERCISE_STUDENT_TIME');
            }
            this.$loading.close();
        },
        getLinks() {
            this.$store.dispatch('LOAD_LINKS', {
                parents: this.parents,
                submodules: this.submodules,
            });
        },
    },

    mounted() {
        if (this.data) {
            this.getLinks();
            return this.$store.commit('SET_EXERCISE', {
                exercise: this.data,
            });
        }
        return null;
    },
};
</script>

<style>
.nch-alert {
    color: crimson;
    font-style: italic;
}

.btn-group {
    cursor: pointer;
}
</style>

<style scoped>
.stretch {
    height: 100%;
    overflow: auto;
    border-radius: 0;
}
</style>
